import type { GlobalSuggestHit } from '@/gql/graphql';
import { Text } from '@/components/ui/Text';
import { SuggestKindBadge } from './SuggestKindBadge';
import { cn } from '@/lib/cn';

/**
 * Shared row renderer for a single search hit (D10 — flat list, no group headers).
 * Layout: displayName + kind badge on top line, subtitle on second line.
 */
interface SearchHitRowProps {
  hit: GlobalSuggestHit;
  isHighlighted: boolean;
  /** 1-indexed rank within the flat result list (used for telemetry in T03). */
  rank: number;
  onSelect: (hit: GlobalSuggestHit, rank: number) => void;
  /** Forwarded ref for scroll-into-view on keyboard highlight. */
  rowRef?: React.RefCallback<HTMLDivElement>;
}

export function SearchHitRow({
  hit,
  isHighlighted,
  rank,
  onSelect,
  rowRef,
}: SearchHitRowProps) {
  return (
    <div
      ref={rowRef}
      id={`search-hit-${rank - 1}`}
      role="option"
      aria-selected={isHighlighted}
      className={cn(
        'flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors select-none',
        isHighlighted ? 'bg-surface-muted' : 'hover:bg-surface-muted/50',
      )}
      onClick={() => onSelect(hit, rank)}
      // Prevent focus loss from input on mouse-down
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <Text variant="body-md" as="span" truncate={1} className="flex-1 min-w-0">
            {hit.displayName}
          </Text>
          <SuggestKindBadge kindLabel={hit.kindLabel} iconKind={hit.iconKind} />
        </div>
        {hit.subtitle && (
          <Text
            variant="body-sm"
            color="muted"
            as="span"
            truncate={1}
            className="block mt-0.5"
          >
            {hit.subtitle}
          </Text>
        )}
      </div>
    </div>
  );
}
